import { cloneDeep } from 'lodash';
import currency from 'currency.js';
import { SituacaoItemCompra } from 'px-business-components';
import { DEFAULT_PRECISION } from '@/utils/produto-utils';
import { converterValorByTaxa } from '@/utils/conversao-moeda-utils';
import { calculateTaxa } from '@/utils/taxa-utils';

const DEFAULT_PERCENTAGE_PRECISION = 12;
/**
 * Função que recebe um número e uma precisão opcional e retorna o valor formatado
 * @param {number} number - O número que você deseja formatar.
 * @param {number} [precisao=DEFAULT_PRECISION] - A precisão é o número de casas decimais.
 * Por padrão, é definido como DEFAULT_PRECISION, que são 10 casas decimais.
 * @returns {currency} - O valor formatado com a precisão especificada.
 */
function currencyDP(number, precisao = DEFAULT_PRECISION) {
  return currency(number, { precision: precisao });
}

/**
 * Verifica se o produto está ativo.
 * @param {Object} produto - O objeto do produto a ser verificado.
 * @returns {boolean} - Retorna true se o produto estiver ativo, caso contrário, false.
 */
function isAtivo(produto) {
  return produto?.situacao === SituacaoItemCompra.keys.ATIVO.key || !produto.situacao;
}

/**
 * Calcula o total da compra, somando o total dos produtos ativos e o frete.
 * @param {Object} compra - O objeto da compra que contém informações sobre o frete.
 * @param {Array} produtos - A lista de produtos incluídos na compra.
 * @returns {number} - O total da compra, incluindo o total dos produtos ativos e o frete.
 */
function total(compra, produtos) {
  if (!produtos.length) {
    return compra.frete.totalTransporte || 0;
  }
  const totalProdutos = produtos
    .filter(produto => isAtivo(produto))
    .map(produto => currencyDP(produto.total).value)
    .reduce((a, b) => currencyDP(a)
      .add(currencyDP(b)).value, 0) || 0;

  return currencyDP(totalProdutos)
    .add(currencyDP(compra.frete.totalTransporte))
    .value || 0;
}

/**
 * Calcula o total dos produtos ativos.
 * @param {Array} produtos - A lista de produtos a serem considerados.
 * @return {number} - O total dos produtos ativos.
 */
function calculateTotalProdutos(produtos) {
  return produtos
    .filter(produto => isAtivo(produto))
    .map(a => currencyDP(a.totalProdutos).value)
    .reduce((a, b) => currencyDP(a)
      .add(currencyDP(b)).value, 0);
}

/**
 * Calcula o total dos produtos ativos, incluindo impostos.
 * @param {Array} produtos - A lista de produtos a serem considerados.
 * @return {number} - O total dos produtos ativos, incluindo impostos.
 */
function calculateTotalProdutosComImpostos(produtos) {
  return produtos
    .filter(produto => isAtivo(produto))
    .map(a => a.total)
    .reduce((a, b) => currencyDP(a)
      .add(currencyDP(b)).value, 0) || 0;
}

/**
 * Calcula o total de um produto com base na quantidade e preço unitário.
 * @param {Object} produto - O objeto do produto que contém quantidade e preço unitário.
 * @returns {number} - O total do produto calculado.
 */
function calculateTotalProduto(produto) {
  return currencyDP(produto.quantidade)
    .multiply(currencyDP(produto.precoUnitario))
    .value;
}

/**
 * Calcula o valor do ICMS para um produto.
 * @param {Object} produto - O objeto do produto que contém o imposto e total de produtos.
 * @returns {number} - O valor do ICMS calculado para o produto.
 */
function calculateIcms(produto) {
  if (!produto.impostos?.icms) {
    return 0;
  }
  const { totalProdutos } = produto;
  const icms = currencyDP(produto.impostos.icms)
    .multiply(100);
  return icms.multiply(currencyDP(totalProdutos))
    .divide(100).value;
}

/**
 * Calcula o valor do IPI para um produto, incluindo o frete.
 * @param {Object} produto - O objeto do produto que contém o imposto e total de produtos.
 * @returns {number} - O valor do IPI calculado para o produto.
 */
function calculateIpi(produto) {
  if (!produto.impostos?.ipi) {
    return 0;
  }
  const { totalProdutos } = produto;
  const ipi = currencyDP(produto.impostos.ipi).multiply(100);
  const valorBase = currencyDP(totalProdutos)
    .add(currencyDP(produto.frete));

  return ipi.multiply(valorBase).divide(100).value;
}

/**
 * Calcula o valor do IPI sobre total do produto com taxa financeira e frete.
 * @param {Object} produto - O objeto do produto que contém o imposto.
 * @param {number} valorFrete - O valor do frete associado ao produto.
 * @param {number} totalProdutoTaxa - O total do produto com taxa financeira.
 * @returns {number} - O valor do IPI calculado para o produto.
 */
function calculateIpiTaxa(produto, valorFrete, totalProdutoTaxa) {
  if (!produto.impostos?.ipi) {
    return 0;
  }
  const valorProdutoComFrete = currencyDP(totalProdutoTaxa).add(currencyDP(valorFrete));
  const ipi = currencyDP(produto.impostos.ipi).multiply(100);
  return ipi.multiply(valorProdutoComFrete).divide(100).value;
}

/**
 * Calcula o total de um produto, incluindo impostos.
 * @param {Object} produto - O objeto do produto que contém o total de produtos e impostos.
 * @returns {number} - O total do produto, incluindo impostos.
 */
function calculateTotal(produto) {
  return currencyDP(produto.totalProdutos)
    .add(currencyDP(produto.impostos.valorIpi))
    .value;
}

/**
 * Calcula o percentual de rateio do frete para um produto.
 * @param {Object} produto - O objeto do produto que contém o total de produtos.
 * @param {Object} compra - O objeto da compra que contém informações sobre o frete.
 * @param {Array} produtos - A lista de produtos para cálculo do total.
 * @returns {number} - O percentual de rateio do frete para o produto.
 */
function calculatePercentualRateio(produto, compra, produtos) {
  if (compra.frete.totalTransporte === 0) {
    return 0;
  }
  return currencyDP(produto.totalProdutos)
    .multiply(100)
    .divide(currencyDP(calculateTotalProdutos(produtos)))
    .value;
}

/**
 * Calcula o valor do frete rateado para um produto com base no percentual de rateio.
 * @param {number} percentualRateio - O percentual de rateio do frete para o produto.
 * @param {number} totalTransporte - O valor total do transporte (frete) da compra.
 * @returns {number} - O valor do frete rateado para o produto.
 */
function calculateFreteItem(percentualRateio, totalTransporte) {
  if (totalTransporte === 0) {
    return 0;
  }
  return currencyDP(percentualRateio)
    .multiply(totalTransporte)
    .divide(100)
    .value;
}

/**
 * Calcula o total e impostos para cada produto ativo na lista de produtos.
 * @param {Array} produtos - A lista de produtos a serem processados.
 */
function calculateProdutos(produtos) {
  produtos
    .filter(produto => isAtivo(produto))
    .forEach((produto) => { /* eslint-disable no-param-reassign */
      produto.totalProdutos = calculateTotalProduto(produto);
      produto.impostos.valorIcms = calculateIcms(produto);
    });
}

/**
 * Calcula o percentual de rateio do frete, valor do frete,
 * valor do IPI e total para cada produto ativo na lista de produtos.
 * @param {Array} produtos - A lista de produtos a serem processados.
 * @param {Object} compra - O objeto da compra que contém informações sobre o frete.
 */
function calculateRateioIpiTotal(produtos, compra) {
  produtos
    .filter(produto => isAtivo(produto))
    .forEach((produto) => { /* eslint-disable no-param-reassign */
      produto.percentualRateio = calculatePercentualRateio(produto, compra, produtos);
      produto.frete = calculateFreteItem(produto.percentualRateio, compra.frete.totalTransporte);
      produto.impostos.valorIpi = calculateIpi(produto);
      produto.total = calculateTotal(produto);
    });
}

/**
 * Calcula o valor total economizado com base no saving unitário e quantidade do produto.
 * @param {Object} produto - O objeto do produto que contém o saving unitário e quantidade.
 */
function calculateSavingForProduto(produto) {
  return currencyDP(produto.$$itemTabelaPreco.saving)
    .multiply(produto.quantidade)
    .value;
}

/**
 * Soma o valor total economizado de uma lista de produtos e converte pela taxa de venda.
 * @param {Array} produtos - A lista de produtos a serem considerados.
 * @param {number} taxaVenda - A taxa de venda para conversão do valor.
 * @return {number} - O valor total economizado somado de todos os produtos,
 * convertido pela taxa de venda.
 */
function sumSavingProdutos(produtos, taxaVenda) {
  const totalSaving = produtos.filter(produto => isAtivo(produto)).reduce(
    (totais, produto) => currencyDP(totais)
      .add(produto.saving || 0).value, 0,
  );
  return converterValorByTaxa(totalSaving, taxaVenda);
}

/**
 * Calcula o valor total economizado de uma lista de produtos ativos
 * com base no saving unitário definido na tabela de preços.
 */
function calculateSavingFromProdutos(produtos) {
  const produtosComTabelaESaving = produtos
    .filter(produto => produto.$$itemTabelaPreco
      && produto.$$itemTabelaPreco.saving
      && isAtivo(produto));

  return produtosComTabelaESaving
    .map(produto => calculateSavingForProduto(produto))
    .reduce((a, b) => currencyDP(a)
      .add(currencyDP(b)).value, 0);
}

/**
 * Calcula o total de um produto, incluindo taxa financeira, IPI e frete.
 * @param {Object} compra - O objeto da compra que contém prazo de pagamento e taxa.
 * @param {Object} produto - O objeto do produto que contém preço unitário e impostos.
 * @returns {number} - O total do produto, incluindo taxa financeira, IPI e frete.
 */
function calculateTaxaFinanceiraProduto(compra, produto) {
  if (!produto?.precoUnitario) {
    return 0;
  }
  const valorFreteProduto = calculateFreteItem(
    produto.percentualRateio ?? currencyDP(produto.impostos.percentualRateio).multiply(100).value,
    compra.frete.totalTransporte,
  );
  const totalTaxaProduto = currencyDP(
    calculateTaxa(compra.prazoPagamento, calculateTotalProduto(produto), compra.percentualTaxa),
  );
  return totalTaxaProduto
    .add(valorFreteProduto).value;
}

/**
 * Calcula o total de um produto, incluindo taxa financeira, IPI e frete.
 * @param {Object} compra - O objeto da compra que contém prazo de pagamento e taxa.
 * @param {Object} produto - O objeto do produto que contém preço unitário e impostos.
 * @returns {number} - O total do produto, incluindo taxa financeira, IPI e frete.
 */
function calculateTaxaFinanceiraProdutoComIpi(compra, produto) {
  if (!produto?.precoUnitario) {
    return 0;
  }
  const valorFreteProduto = calculateFreteItem(
    produto.percentualRateio ?? currencyDP(produto.impostos.percentualRateio).multiply(100).value,
    compra.frete.totalTransporte,
  );
  const totalTaxaProduto = currencyDP(
    calculateTaxa(compra.prazoPagamento, calculateTotalProduto(produto), compra.percentualTaxa),
  );
  return totalTaxaProduto
    .add(calculateIpiTaxa(produto, valorFreteProduto, totalTaxaProduto))
    .add(valorFreteProduto).value;
}

/**
 * Calcula o total dos produtos, incluindo taxa financeira e frete, e converte pela taxa de venda.
 * @param {Object} compra - O objeto da compra que contém prazo de pagamento e taxa.
 * @param {Object} produtos - A lista de produtos a serem considerados.
 * @param {number} taxaVenda - A taxa de venda para conversão do valor.
 * Quando não informada, o valor não é convertido.
 * @returns {number} - O total dos produtos, incluindo taxa financeira e frete.
 * Podendo ser convertido pela taxa de venda.
 */
function calcularTotaisTaxaFinanceira(compra, produtos, taxaVenda) {
  if (!compra.percentualTaxa || !compra.prazoPagamento || !produtos) {
    return 0;
  }
  return produtos.filter(produto => isAtivo(produto)).reduce(
    (totais, produto) => currencyDP(totais).add(
      converterValorByTaxa(calculateTaxaFinanceiraProduto(compra, produto), taxaVenda),
    ).value, 0,
  );
}

/**
 * Calcula o total dos produtos, incluindo taxa financeira,
 * IPI e frete, e converte pela taxa de venda.
 * @param {Object} compra - O objeto da compra que contém prazo de pagamento e taxa.
 * @param {Object} produtos - A lista de produtos a serem considerados.
 * @param {number} taxaVenda - A taxa de venda para conversão do valor.
 * Quando não informada, o valor não é convertido.
 * @returns {number} - O total dos produtos, incluindo taxa financeira, IPI e frete.
 * Podendo ser convertido pela taxa de venda.
 */
function calcularTotaisTaxaFinanceiraComIpi(compra, produtos, taxaVenda) {
  if (!compra.percentualTaxa || !compra.prazoPagamento || !produtos) {
    return 0;
  }
  return produtos.filter(produto => isAtivo(produto)).reduce(
    (totais, produto) => currencyDP(totais).add(
      converterValorByTaxa(calculateTaxaFinanceiraProdutoComIpi(compra, produto), taxaVenda),
    ).value, 0,
  );
}

/**
 * Calcula o total convertido de um produto, incluindo taxa financeira, IPI e frete.
 * @param {Object} compra - O objeto da compra que contém prazo de pagamento e taxa.
 * @param {Object} produto - O objeto do produto que contém preço unitário e impostos.
 * @returns {number} - O total do produto, incluindo taxa financeira, IPI e frete.
 */
function calculateTaxaFinanceiraProdutoConvertido(compra, produto) {
  if (!produto?.valorMoedaConvertido?.precoUnitario) {
    return 0;
  }
  const valorFreteProduto = calculateFreteItem(
    currencyDP(produto.impostos.percentualRateio).multiply(100).value,
    compra.frete.totalTransporteConvertido,
  );
  const totalTaxaProduto = currencyDP(
    calculateTaxa(compra.prazoPagamento, produto.valorMoedaConvertido.totalProdutos,
      compra.percentualTaxa),
  );
  return totalTaxaProduto
    .add(calculateIpiTaxa(produto, valorFreteProduto, totalTaxaProduto))
    .add(valorFreteProduto).value;
}

/**
 * Calcula o total convertido dos produtos, incluindo taxa financeira, IPI e frete.
 * @param {Object} compra - O objeto da compra que contém prazo de pagamento e taxa.
 * @param {Object} produtos - A lista de produtos a serem considerados.
 * @returns {number} - O total dos produtos, incluindo taxa financeira, IPI e frete.
 */
function calcularTotaisTaxaFinanceiraConvertido(compra, produtos) {
  if (!compra.percentualTaxa || !compra.prazoPagamento || !produtos) {
    return 0;
  }
  return produtos.filter(produto => isAtivo(produto)).reduce(
    (totais, produto) => currencyDP(totais).add(
      converterValorByTaxa(calculateTaxaFinanceiraProdutoConvertido(compra, produto)),
    ).value, 0,
  );
}

/**
 * Calcula os totais da compra, incluindo total dos produtos,
 * total convertido, total dos produtos convertidos, total com taxa financeira
 * e total com taxa financeira convertido.
 * @param {Object} compraParam - O objeto da compra que contém informações sobre o frete e taxa.
 * @param {Array} produtosParam - A lista de produtos incluídos na compra.
 * @param {number} taxaVenda - A taxa de venda para conversão dos valores.
 * @returns {Object} - Um objeto contendo os totais calculados:
 * total, totalProdutos, totalConvertido, totalProdutosConvertido,
 * totalTaxaFinanceira, totalTaxaFinanceiraConvertido.
 */
function calcularTotaisCompra(compraParam, produtosParam, taxaVenda) {
  const compra = cloneDeep(compraParam);
  const produtos = cloneDeep(produtosParam);

  if (!compra || !produtos.length) {
    return {
      total: 0,
      totalProdutos: 0,
      totalConvertido: 0,
      totalProdutosConvertido: 0,
      totalTaxaFinanceira: 0,
      totalTaxaFinanceiraIpi: 0,
      totalTaxaFinanceiraConvertido: 0,
      totalTaxaFinanceiraConvertidoIpi: 0,
    };
  }

  calculateProdutos(produtos);
  calculateRateioIpiTotal(produtos, compra);

  const totalCompra = total(compra, produtos);
  const totalProdutosCompra = calculateTotalProdutosComImpostos(produtos);
  return {
    total: totalCompra,
    totalProdutos: totalProdutosCompra,
    totalConvertido: converterValorByTaxa(totalCompra, taxaVenda),
    totalProdutosConvertido: converterValorByTaxa(totalProdutosCompra, taxaVenda),
    totalTaxaFinanceira: calcularTotaisTaxaFinanceira(compra, produtos),
    totalTaxaFinanceiraIpi: calcularTotaisTaxaFinanceiraComIpi(compra, produtos),
    totalTaxaFinanceiraConvertido: calcularTotaisTaxaFinanceira(compra, produtos, taxaVenda),
    totalTaxaFinanceiraConvertidoIpi:
      calcularTotaisTaxaFinanceiraComIpi(compra, produtos, taxaVenda),
  };
}

/**
 * Calcula os totais de saving da compra.
 * @param {Object} compra - O objeto da compra que contém informações sobre o saving.
 * @param {number} taxaVenda - A taxa de venda para conversão do valor.
 * @returns {Object} - Um objeto contendo os totais de saving:
 * saving e savingConvertido.
 */
function calcularTotaisSaving(compra, taxaVenda) {
  if (compra.saving) {
    return {
      saving: compra.saving,
      savingConvertido: converterValorByTaxa(compra.saving, taxaVenda),
    };
  }
  const saving = calculateSavingFromProdutos(compra.produtos);
  return {
    saving,
    savingConvertido: converterValorByTaxa(saving, taxaVenda),
  };
}

/**
 * Calcula os totais de transporte da compra.
 * @param {Object} compra - O objeto da compra que contém informações sobre o frete.
 * @param {number} taxaVenda - A taxa de venda para conversão do valor.
 * @returns {Object} - Um objeto contendo os totais de transporte:
 * totalTransporte e totalTransporteConvertido.
 */
function calcularTotaisTransporte(compra, taxaVenda) {
  if (!compra.frete.totalTransporte || compra.frete.totalTransporte === 0) {
    return {
      totalTransporte: 0,
      totalTransporteConvertido: 0,
    };
  }
  return {
    totalTransporte: compra.frete.totalTransporte,
    totalTransporteConvertido: converterValorByTaxa(compra.frete.totalTransporte, taxaVenda),
  };
}

/**
 * Calcula o total dos produtos ativos.
 * @param {Array} produtos - A lista de produtos a serem considerados.
 * @return {number} - O total dos produtos ativos.
 */
function calculateTotalDosProdutos(produtos) {
  return produtos
    .filter(produto => isAtivo(produto))
    .reduce((totaisProdutos, produto) => currencyDP(totaisProdutos)
      .add(calculateTotalProduto(produto)).value, 0);
}

/**
 * Calcula o preço unitário bruto de um produto,
 * somando o preço unitário líquido com o saving unitário.
 * @param {Object} produto - O objeto do produto que contém preço unitário e saving unitário.
 * @returns {number} - O preço unitário bruto do produto.
 */
function calculatePrecoUnitarioBruto(produto) {
  return currencyDP(
    produto.precoUnitario,
  ).add(produto.$$savingUnitario).value;
}

/**
 * Calcula o saving unitário de um produto com base no saving total e quantidade.
 * @param {Object} produto - O objeto do produto que contém saving total e quantidade.
 * @returns {number} - O saving unitário do produto.
 */
function calculateSavingUnitarioPeloSaving(produto) {
  return currencyDP(
    produto.saving,
  ).divide(produto.quantidade || 1).value;
}

/**
 * Calcula o saving unitário de um produto com base no percentual de saving
 * e preço unitário bruto.
 * @param {Object} produto - O objeto do produto que contém percentual de saving
 * e preço unitário bruto.
 * @returns {number} - O saving unitário do produto.
 */
function calculateSavingUnitarioPeloPercentualSaving(produto) {
  return currencyDP(
    produto.$$precoUnitarioBruto,
  ).multiply(produto.percentualSaving || 0).value;
}

/**
 * Calcula o percentual de saving de um produto com base no saving unitário
 * e preço unitário bruto.
 * @param {Object} produto - O objeto do produto que contém saving unitário
 * e preço unitário bruto.
 * @returns {number} - O percentual de saving do produto.
 */
function calculatePercentualSaving(produto) {
  if ((produto.$$precoUnitarioBruto || 0) === 0) {
    return 0;
  }
  return currencyDP(
    produto.$$savingUnitario || 0, DEFAULT_PERCENTAGE_PRECISION,
  ).divide(produto.$$precoUnitarioBruto).value;
}

/**
 * Calcula o saving total de um produto com base no saving unitário e quantidade.
 * @param {Object} produto - O objeto do produto que contém saving unitário e quantidade.
 * @returns {number} - O saving total do produto.
 */
function calculateSaving(produto) {
  return currencyDP(
    produto.$$savingUnitario || 0,
  ).multiply(produto.quantidade || 1).value;
}

/**
 * Calcula o preço unitário líquido de um produto,
 * subtraindo o saving unitário do preço unitário bruto.
 * @param {Object} produto - O objeto do produto que contém preço unitário bruto e saving unitário.
 * @returns {number} - O preço unitário líquido do produto.
 */
function calculatePrecoUnitario(produto) {
  return currencyDP(
    produto.$$precoUnitarioBruto || 0,
  ).subtract(produto.$$savingUnitario || 0).value;
}

/**
 * Calcula os valores detalhados de um produto, incluindo total, total líquido,
 * total bruto, valor do ICMS e valor do IPI.
 * @param {Object} compraParam - O objeto da compra que contém informações sobre o frete e produtos.
 * @param {Object} produtoParam - O objeto do produto a ser processado.
 * @return {Object} - Um objeto contendo os valores detalhados do produto:
 * total, totalLiquido, totalBruto, valorIcms e valorIpi.
 */
function calculateValoresProduto(compraParam, produtoParam) {
  if (!compraParam || !compraParam?.produtos?.length) {
    return {
      total: 0,
      totalLiquido: 0,
      totalLiquidoTaxa: 0,
      totalBruto: 0,
      valorIcms: 0,
      valorIpi: 0,
      valorIpiTaxa: 0,
    };
  }
  const compra = cloneDeep(compraParam);
  const produto = cloneDeep(produtoParam);

  calculateProdutos(compra.produtos);

  produto.totalProdutos = calculateTotalProduto(produto);
  produto.frete = calculateFreteItem(
    calculatePercentualRateio(produto, compra, compra.produtos),
    compra.frete.totalTransporte,
  );
  produto.impostos.valorIcms = calculateIcms(produto);
  produto.impostos.valorIpi = calculateIpi(produto);
  produto.total = calculateTotal(produto);
  const totalTaxaProduto = calculateTaxa(
    compra.prazoPagamento,
    produto.totalProdutos,
    compra.percentualTaxa,
  );
  return {
    total: produto.total,
    totalLiquido: produto.totalProdutos,
    totalLiquidoTaxa: totalTaxaProduto,
    totalBruto: currencyDP(produto.totalProdutos).add(produto.saving || 0).value,
    valorIcms: produto.impostos.valorIcms,
    valorIpi: produto.impostos.valorIpi,
    valorIpiTaxa: calculateIpiTaxa(produto, produto.frete, totalTaxaProduto),
  };
}

/**
 * Calcula o desconto proporcional e o percentual de saving do produto
 * com base no saving total da compra.
 * Caso a quantidade não seja informada, considera 1 como padrão.
 * @param {Object} compra - O objeto da compra que contém o saving total.
 */
function calculatePercentualDescontoProduto(compra, p) {
  const produto = p;
  const quantidade = currencyDP(produto.quantidade || 1);
  const precoUnitario = currencyDP(produto.precoUnitario || 0);
  if (quantidade.value > 0 && precoUnitario.value > 0) {
    const totalProdutos = calculateTotalDosProdutos(compra.produtos);
    produto.saving = currencyDP(compra.saving || 0)
      .multiply(currencyDP(calculateTotalProduto(produto)).divide(totalProdutos)).value;
    produto.$$savingUnitario = calculateSavingUnitarioPeloSaving(produto);
  }
}

/**
 * Calcula o desconto proporcional e o percentual de saving dos produtos
 * com base no saving total da compra.
 * Caso tenha apenas um produto, atribui o saving total diretamente a ele.
 * Caso a quantidade não seja informada, considera 1 como padrão.
 * @param {Object} compra - O objeto da compra que contém o saving total e a lista de produtos.
 */
function calculatePercentualDescontoProdutos(compra) {
  if (!Array.isArray(compra.produtos) || Number(compra.saving) <= 0
    || compra.produtos.some(p => isAtivo(p) && p.saving && Number(p.saving) > 0)) {
    return;
  }
  if (compra.produtos.length === 1) {
    const produto = compra.produtos[0];
    produto.saving = compra.saving;
    produto.$$savingUnitario = calculateSavingUnitarioPeloSaving(produto);
  } else {
    compra.produtos.forEach((p) => {
      calculatePercentualDescontoProduto(compra, p);
    });
  }
}

export {
  total,
  calcularTotaisCompra,
  calcularTotaisSaving,
  calcularTotaisTransporte,
  calcularTotaisTaxaFinanceira,
  calcularTotaisTaxaFinanceiraConvertido,
  calculateTotalDosProdutos,
  calculatePercentualDescontoProdutos,
  sumSavingProdutos,
  currencyDP,
  calculateValoresProduto,
  calculatePrecoUnitarioBruto,
  calculateSavingUnitarioPeloSaving,
  calculateSavingUnitarioPeloPercentualSaving,
  calculatePercentualSaving,
  calculateSaving,
  calculatePrecoUnitario,
};
